import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    OrderRouteService, BrokerGetOutputModel
} from '@spa/api';

import {RequiredValidatorKey} from '@spa/validators';
import {
    BrokerStatusControlOptions,
    ExecutionBrokerStatusesControl
} from 'appAssets/descriptors';
import {Helper, RadioGroupControl} from 'appShared/classes';
import {RadioGroupOptionInterface} from 'appShared/components';

export class BrokerStatusForm {
    private orderRouteService: OrderRouteService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(orderRouteService: OrderRouteService) {
        this.orderRouteService = orderRouteService;
    }

    private mapStatusControlOptions(
        broker: BrokerGetOutputModel,
        triggers: number[],
        currentTrigger: number,
        options: RadioGroupOptionInterface[],
        disabledOption: boolean
    ): RadioGroupOptionInterface[] {
        return options.map(option => {
            const trigger = Helper.enumsHelper()
                .getBrokerStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledOption) {
                option.disabled = true;
            }

            if (option.value === broker.getStatus()) {
                option.checked = true;

                this.control.value = broker.getStatus();
            }

            return option;
        });
    }

    initForm(broker: BrokerGetOutputModel, brokerRequestTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = ExecutionBrokerStatusesControl();

        const currentTrigger = Helper.enumsHelper()
            .getBrokerStatusTriggerByStatus(broker.getStatus());

        const options = BrokerStatusControlOptions();


        this.control.options = this.mapStatusControlOptions(
            broker,
            brokerRequestTriggers,
            currentTrigger,
            options,
            disabledControlItems
        );

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    refreshForm(broker: BrokerGetOutputModel, triggers: number[], disabledControlItems: boolean): void {
        const currentTrigger = Helper.enumsHelper()
            .getBrokerStatusTriggerByStatus(broker.getStatus());

        this.control.options.forEach(option => {
            const trigger = Helper.enumsHelper()
                .getBrokerStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledControlItems) {
                option.disabled = true;
            } else {
                option.disabled = false;
            }

            if (option.value === broker.getStatus()) {
                option.checked = true;

                this.control.value = broker.getStatus();
            }
        });
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    createRequest(
        broker: BrokerGetOutputModel
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name];

        if (broker.getStatus() !== status) {
            const updateStatus = Helper.enumsHelper().getTradingPlanStatusTriggerByStatus(status);

            return this.orderRouteService.updateBrokerStatus(broker.getId(), updateStatus)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse(
                                response
                            );
                        }
                        broker.setStatus(status);

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
